import styled from "styled-components"
import Header from "../Common/Header"
import Footer from "../Common/Footer"

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #f6f8fa;
  color: #102840;
`

/**
 * Same approach: hero is independent, all content sections live here.
 */
const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(4rem, 8vw, 7rem);
  padding: clamp(4rem, 8vw, 7rem) 0;
`

const Section = styled.section`
  width: min(1280px, 92vw);
  margin: 0 auto;
  display: grid;
  gap: clamp(1.5rem, 4vw, 2.5rem);
`

/* ────────────────────────────── HERO ────────────────────────────── */

const HeroSection = styled.section`
  position: relative;
  min-height: 680px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: clamp(6rem, 11vw, 9rem) max(4vw, calc((100vw - 1280px) / 2));
  color: #ffffff;
  overflow: hidden;
  background: url("/img/bg.jpg") center / cover no-repeat;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, rgba(6, 25, 43, 0.96) 0%, rgba(7, 39, 68, 0.86) 48%, rgba(7, 39, 68, 0.22) 100%);
    z-index: 0;
  }
`

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  width: min(790px, 92vw);
  display: grid;
  gap: 1.8rem;
  text-align: left;
`

const HeroBadge = styled.span`
  display: inline-flex;
  justify-content: flex-start;
  align-items: center;
  gap: 0.6rem;
  padding: 0 0 0.8rem;
  border-bottom: 1px solid rgba(124, 196, 240, 0.55);
  color: #8acbf3;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  font-size: 0.8rem;
`

const HeroTitle = styled.h1`
  margin: 0;
  max-width: 780px;
  font-size: clamp(3rem, 6.8vw, 6.4rem);
  font-weight: 560;
  letter-spacing: -0.055em;
  line-height: 0.96;
  color: #fff;
`

const HeroSubtitle = styled.p`
  margin: 0;
  max-width: 680px;
  font-size: clamp(1.05rem, 2vw, 1.35rem);
  line-height: 1.65;
  color: rgba(255, 255, 255, 0.88);
`

const HeroPills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.7rem;
  justify-content: flex-start;
`

const HeroPill = styled.span`
  padding: 0.45rem 0.7rem;
  border-radius: 2px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  font-size: 0.82rem;
  font-weight: 500;
  background: rgba(5, 30, 60, 0.62);
`

const HeroCTAGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 0.9rem;
`

const PrimaryCTA = styled.a`
  background: #ffffff;
  color: #11375b;
  padding: 0.9rem 1.7rem;
  border-radius: 2px;
  font-weight: 600;
  font-size: 0.98rem;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  box-shadow: 0 14px 32px rgba(0, 0, 0, 0.22);
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    opacity 0.2s ease;

  &:hover {
    transform: translateY(-1.5px);
    box-shadow: 0 18px 40px rgba(0, 0, 0, 0.45);
    opacity: 0.96;
  }
`

/* ────────────────────────────── BODY ────────────────────────────── */

const GuideSummary = styled.div`
  background: #fff;
  border: 1px solid rgba(16, 55, 91, 0.15);
  border-top: 4px solid #1f6fa9;
  border-radius: 2px;
  padding: clamp(2rem, 5vw, 4rem);
  box-shadow: 0 22px 50px rgba(13, 65, 114, 0.08);
  display: grid;
  grid-template-columns: minmax(220px, 0.65fr) minmax(0, 1.35fr);
  gap: clamp(2rem, 6vw, 6rem);

  h2 {
    margin: 0;
    font-size: clamp(2rem, 3.5vw, 3.6rem);
    line-height: 1.05;
    letter-spacing: -0.04em;
  }

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`

const List = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 0;
  font-size: 1rem;
  color: #16324f;

  li {
    padding: 1rem 0 1rem 1.5rem;
    border-top: 1px solid rgba(16, 55, 91, 0.14);
    position: relative;
  }

  li::before {
    content: "";
    position: absolute;
    left: 0;
    top: 1.45rem;
    width: 8px;
    height: 8px;
    background: #337abd;
  }
`

const ResourceGrid = styled.div`
  display: grid;
  gap: 0;

  @media (min-width: 900px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`

const ResourceCard = styled.article`
  background: #fff;
  border-radius: 0;
  padding: clamp(1.5rem, 3vw, 2.4rem);
  display: grid;
  gap: 0.9rem;
  box-shadow: none;
  border-left: 1px solid rgba(27, 117, 188, 0.18);
  border-top: 3px solid #337abd;

  &:first-child {
    border-left: none;
  }

  @media (max-width: 899px) {
    border-left: none;
    border-top-width: 1px;
  }
`

const ResourceLink = styled.a`
  color: ${({ theme }) => theme.primary};
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

const SecondarySection = styled.div`
  background: #eaf1f7;
  border: 1px solid rgba(16, 55, 91, 0.14);
  border-radius: 2px;
  padding: clamp(2rem, 5vw, 4rem);
  display: grid;
  gap: 1.2rem;
`

const GuideProprietairesPortneuf = () => {
  return (
    <PageWrapper>
      <Header />

      <main id="contenu-principal">
        <HeroSection>
          <HeroContent>
            <HeroBadge>Ressources pour propriétaires</HeroBadge>
            <HeroTitle>Des repères concrets pour mieux suivre vos immeubles.</HeroTitle>
            <HeroSubtitle>
              Entretien saisonnier, échéances locatives et organisation des locations touristiques dans Portneuf et l’ouest de Québec.
            </HeroSubtitle>

            <HeroPills>
              <HeroPill>Portneuf et couronne ouest de Québec</HeroPill>
            </HeroPills>

            <HeroCTAGroup>
              <PrimaryCTA href="#ressources">Explorer les ressources locales</PrimaryCTA>
            </HeroCTAGroup>
          </HeroContent>
        </HeroSection>

        <ContentWrapper>
          <Section>
            <GuideSummary>
              <h2>Ce que vous trouverez ici</h2>
              <List>
                <li>Une liste de vérification pour préparer un plex à l’hiver.</li>
                <li>Des repères pour vérifier les règles, permis et échéances applicables.</li>
                <li>Une méthode simple pour suivre les tarifs et opérations d’un chalet locatif.</li>
                <li>Des liens vers les services de gestion offerts dans la région.</li>
              </List>
            </GuideSummary>
          </Section>

          <Section id="ressources">
            <SecondarySection>
              <h2>Ressources pour vos immeubles</h2>
              <p>
                Ces articles regroupent des listes de vérification et des sources à consulter pour les immeubles de Portneuf et de la couronne ouest
                de Québec.
              </p>
              <ResourceGrid>
                <ResourceCard>
                  <h3>Vérifier la réglementation d’un immeuble à revenus</h3>
                  <p>Les vérifications de base pour les baux, la sécurité, l’entretien et les autorisations municipales.</p>
                  <ResourceLink href="/blog/reglementation-immeubles-portneuf">Lire l&apos;article</ResourceLink>
                </ResourceCard>
                <ResourceCard>
                  <h3>Préparer son plex pour l'hiver à Neuville</h3>
                  <p>Une liste de vérification en quatre étapes pour le bâtiment, les équipements et les communications.</p>
                  <ResourceLink href="/blog/preparer-plex-hiver-neuville">Lire l&apos;article</ResourceLink>
                </ResourceCard>
                <ResourceCard>
                  <h3>Mieux structurer la gestion d’un chalet locatif</h3>
                  <p>Tarifs, expérience des voyageurs, visibilité et suivi des données utiles.</p>
                  <ResourceLink href="/blog/optimiser-chalets-locatifs">Lire l&apos;article</ResourceLink>
                </ResourceCard>
              </ResourceGrid>
            </SecondarySection>
          </Section>
        </ContentWrapper>
      </main>

      <Footer />
    </PageWrapper>
  )
}

export default GuideProprietairesPortneuf
